import { Corde, Discipline, DISCIPLINE_OPTION, DISCIPLINE_OPTION_TO_DISCIPLINES, SORT_OPTION, StatusCourse, Terrain } from '../domains/courses/services/enum';
import {
  Course,
  CourseFilter,
  FlattedCourse,
  Report,
  Reunion,
} from '../domains/courses/services/type';

export const getDateOfDay = (dateString: string): string => {
  const jours = [
    'Dimanche',
    'Lundi',
    'Mardi',
    'Mercredi',
    'Jeudi',
    'Vendredi',
    'Samedi',
  ];

  const mois = [
    'Janvier',
    'Février',
    'Mars',
    'Avril',
    'Mai',
    'Juin',
    'Juillet',
    'Août',
    'Septembre',
    'Octobre',
    'Novembre',
    'Décembre',
  ];

  // Parse the input date string in JJMMAAAA format
  const jour = parseInt(dateString.slice(0, 2), 10);
  const moisIndex = parseInt(dateString.slice(2, 4), 10) - 1; // Months are 0-indexed
  const annee = parseInt(dateString.slice(4, 8), 10);

  const date = new Date(annee, moisIndex, jour);

  // Get day of the week, day of the month, and month name
  const jourSemaine = jours[date.getDay()];
  const moisNom = mois[date.getMonth()];

  return `${jourSemaine} ${jour} ${moisNom}`;
};

/**
 * Retourne la date du jour au format JJMMAAAA.
 * @returns {string} - Date formatée.
 */
export const getCurrentDate = (): string => {
  const today = new Date();

  const day = today.getDate().toString().padStart(2, '0'); // Jour avec deux chiffres
  const month = (today.getMonth() + 1).toString().padStart(2, '0'); // Mois avec deux chiffres (getMonth() est 0-indexé)
  const year = today.getFullYear().toString(); // Année avec 4 chiffres

  return `${day}${month}${year}`;
};

/**
 * Retourne un tableau d'objets avec le numéro de la réunion et de chaque course.
 * @param reunions - Tableau des réunions
 * @returns {ReunionCourseMapping[]} - Tableau contenant le numéro de la réunion et de chaque course
 */
export const extractReunionCourses = (
  reunions?: Reunion[]
): FlattedCourse[] => {
  const result: FlattedCourse[] = [];

  reunions?.forEach((reunion) => {
    reunion.courses.forEach((course) => {
      result.push({
        reunionNum: reunion.numOfficiel,
        hippodrome: reunion.hippodrome.libelleLong,
        courseNum: course.numOrdre,
        course: course,
        time: course.heureDepart,
      });
    });
  });

  return result.sort((a, b) => a.time - b.time);
};


export const filterCourses = (
  courses: FlattedCourse[],
  filter: CourseFilter
): FlattedCourse[] => {
  let result = [...courses];

  if (filter.reunion !== undefined) {
    result = result.filter(
      (course) => course.reunionNum === filter.reunion
    );
  }

  if (filter.discipline) {
    const allowedDisciplines =
      DISCIPLINE_OPTION_TO_DISCIPLINES[filter.discipline];

    result = result.filter((course) =>
      allowedDisciplines.includes(course.course.discipline)
    );
  }

  if (filter.minParticipant) {
    result = result.filter(
      (course) => course.course.nombreDeclaresPartants >= (filter.minParticipant || 0)
    );
  }

  if (filter.maxParticipant) {
    result = result.filter(
      (course) => course.course.nombreDeclaresPartants <= (filter.maxParticipant || 30)
    );
  }

  return result.sort((a, b) => a.time - b.time);
};

/**
 * Convertit un timestamp en format HH:mm
 * @param {number} timestamp - Timestamp en millisecondes
 * @returns {string} - Heure au format HH:mm
 */
export const convertTimestampToTime = (timestamp: number): string => {
  const date = new Date(timestamp); // Crée un objet Date à partir du timestamp
  const hours = date.getHours().toString().padStart(2, '0'); // Heures au format 2 chiffres
  const minutes = date.getMinutes().toString().padStart(2, '0'); // Minutes au format 2 chiffres
  return `${hours}:${minutes}`; // Retourne au format HH:mm
};

export const getStatus = (status: StatusCourse): string => {
  if (status === StatusCourse.ANNULEE) return 'Annulée';
  if (status === StatusCourse.ARRIVEE) return 'Arrivée';
  return 'Prgm';
};

/**
 * Extrait une date au format JJMMAAAA depuis un timestamp.
 * @param timestamp - Le timestamp en millisecondes.
 * @returns {string} - La date au format JJMMAAAA.
 */
export const formatDateFromTimestamp = (timestamp: number): string => {
  const date = new Date(timestamp);

  // Ajoute un zéro devant les valeurs si nécessaire
  const padZero = (num: number) => num.toString().padStart(2, '0');

  const day = padZero(date.getDate()); // Jour
  const month = padZero(date.getMonth() + 1); // Mois (0-indexé)
  const year = date.getFullYear(); // Année

  return `${day}${month}${year}`;
};

export const extractPodium = (course: Course): Array<string> => {
  if (course.categorieStatut === StatusCourse.ARRIVEE) {
    if (course.nombreDeclaresPartants >= 8) {
      return course.ordreArrivee.slice(0, 3).join(',').split(',');
    }
    return course.ordreArrivee.slice(0, 2).join(',').split(',');
  }
  return [];
};

export const formatCategorieParticularite = (text: string): string => {
  if (text) {
    return text
      .replaceAll('_', ' ') // Remplace les underscores par des espaces
      .toLocaleLowerCase() // Convertit tout en minuscules
      .replace(/^\w/, (c) => c.toUpperCase()); // Met la première lettre en majuscule
  }
  return '';
};

export const getPoidHandicapLabel = (handicap?: number, poids?: number): string => {
  if (poids && handicap) {
    return `${handicap} / ${poids / 10}kg`
  } else if (poids) {
    return `${poids / 10}kg`
  } else if (handicap) {
    return `${handicap}`
  } else {
    return ''
  }
};

export const isTomorrowSelectedDate = (selectedDate: string): boolean => {
  return getTomorrowDate() === selectedDate;
};

export const getTodayDate = () => {
  // Get today's date
  const today = new Date();

  // Format today's date as JJMMAAAA
  const todayFormatted =
    today.getDate().toString().padStart(2, '0') +
    (today.getMonth() + 1).toString().padStart(2, '0') +
    today.getFullYear();

  // Compare today's date with the selected date
  return todayFormatted;
};

export const getCorde = (corde: Corde) => {
  if (corde === Corde.CORDE_GAUCHE) return 'G';
  if (corde === Corde.CORDE_DROITE) return 'D';
  return '';
};

export const formatTypePiste = (piste: Terrain) => {
  if (piste === Terrain.PSF_STANDARD) return "PSF"
  return piste;
}

export const getTomorrowDate = () => {
  const today = new Date();

  // Ajouter 1 jour
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);

  // Formater au format JJMMAAAA
  const tomorrowFormatted =
    tomorrow.getDate().toString().padStart(2, '0') +
    (tomorrow.getMonth() + 1).toString().padStart(2, '0') +
    tomorrow.getFullYear();

  return tomorrowFormatted;
};

export const isDateWithinThreeDays = (selectedDate: string): boolean => {
  const day = parseInt(selectedDate.slice(0, 2), 10);
  const month = parseInt(selectedDate.slice(2, 4), 10) - 1; // Month is 0-indexed in JS
  const year = parseInt(selectedDate.slice(4), 10);

  const selected = new Date(year, month, day);
  const today = new Date();

  today.setHours(0, 0, 0, 0);
  selected.setHours(0, 0, 0, 0);

  // Calculate the difference in days
  const differenceInMs = selected.getTime() - today.getTime();
  const differenceInDays = differenceInMs / (1000 * 60 * 60 * 24);

  return differenceInDays <= -6;
};

export const getNextRace = (races: FlattedCourse[]): FlattedCourse | null => {
  const currentTime = Date.now(); // Timestamp actuel en secondes

  // Filtrer les courses futures et trier par temps croissant
  const upcomingRaces = races
    .filter((race) => race.time > currentTime)
    .sort((a, b) => a.time - b.time);

  // Retourne la première course à venir, sinon null s'il n'y en a pas
  return upcomingRaces.length > 0 ? upcomingRaces[0] : null;
};

export const filterReportsByDate = (
  reports?: Report[]
): {
  recent30Days: Array<Report>;
  recent6Months: Array<Report>;
  olderReports: Array<Report>;
} => {
  if (reports) {
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 60);

    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(today.getMonth() - 6);

    const recent30Days: Report[] = [];
    const recent6Months: Report[] = [];
    const olderReports: Report[] = [];

    reports.forEach((report) => {
      const reportDate = parseDate(report.date);

      if (reportDate >= thirtyDaysAgo) {
        recent30Days.push(report);
      } else if (reportDate >= sixMonthsAgo) {
        recent6Months.push(report);
      } else {
        olderReports.push(report);
      }
    });

    return { recent30Days, recent6Months, olderReports };
  }
  return { recent30Days: [], recent6Months: [], olderReports: [] };
};

// Fonction pour convertir "DDMMYYYY" en objet Date
const parseDate = (dateStr: string): Date => {
  const day = parseInt(dateStr.substring(0, 2), 10);
  const month = parseInt(dateStr.substring(2, 4), 10) - 1; // 0-based
  const year = parseInt(dateStr.substring(4, 8), 10);
  return new Date(year, month, day);
};

export const formatReduction = (value: number | null): string | null => {
  if (value === null || value === undefined) return null;

  const totalSeconds = value / 1000; // ex: 78500 => 78.5
  const minutes = Math.floor(totalSeconds / 60); // => 1
  const seconds = Math.floor(totalSeconds % 60); // => 18
  const tenths = Math.floor((totalSeconds - Math.floor(totalSeconds)) * 10);

  return `${minutes}.${seconds < 10 ? '0' : ''}${seconds}.${tenths}`;
};

export const extractCourseCategory = (text: string): string | null => {
  const match = text.match(/Course\s+([A-Z])\b/);
  return match ? match[1] : null;
};

export const extractGainMax = (
  text: string
): number | null => {

  const normalizedText = text
    .replace(/[’']/g, "'")
    .replace(/\s+/g, ' ')
    .trim();

  const match = normalizedText.match(
    /(?:n'ayant pas gagné|mais\s+(?:pas|p\s*s|as|a))\s+([\d.,]+)/i
  );

  if (!match) {
    return null;
  }

  const value = match[1]
    .replace(/\./g, '')
    .replace(',', '.');

  return parseFloat(value);
};

export const extractReculValue = (text: string, plafond: number | null): number | null => {
  const match = text.match(
    /Recul de\s+\d+\s*(?:m\.|\.)?\s*(?:à\s*)?([\d.,]+)/i
  );

  if (match) {
    const value = parseFloat(match[1].replace(/\./g, "").replace(",", "."));
    if (value > 0) {
      if (plafond && value <= plafond * 0.2) {
        return value * 10
      } else {
        return value;
      }
    }
  }
  return null;
};

export const formatScoreToDisplay = (score: number, sortSelected: SORT_OPTION) => {
  if (sortSelected === SORT_OPTION.PROBABILITY) {
    return `${score?.toFixed(0)}%`;
  }
  else if (sortSelected === SORT_OPTION.RED) {
    return `${formatReduction(score || 0)}`;
  }
  else {
    return Math.max(1, score).toFixed(1);
  }
};

export const formatHippodromeName = (name: string): string => {
  return name
    .replace(/^hippodrome d['’]\s*/i, '')
    .replace(/^hippodrome de\s+/i, '')
    .replace(/^hippodrome des\s+/i, '')
    .replace(/^hippodrome du\s+/i, '')
    .replace(/^hippodrome\s+/i, '')
    .replace(/^hippo de\s+/i, '')
    .trim();
};

export const capitalizeWords = (value: string): string => {
  return value
    .toLowerCase()
    .split(' ')
    .filter(Boolean)
    .map(
      word => word.charAt(0).toUpperCase() + word.slice(1)
    )
    .join(' ');
};

export const formatMusique = (musique: string, isMobile: boolean): string => {
  if (isMobile && musique && musique.length > 16) {
    return `${musique.slice(0, 16)}...`
  }
  return musique
}

export const formatDiscipline = (discipline: Discipline, isMobile: boolean): string => {
  if (isMobile) {
    return discipline === Discipline.STEEPLECHASE ? "Steeple" : capitalizeWords(discipline);
  }
  return capitalizeWords(discipline)
}

export const getNextDayName = (selectedDate: string): string => {
  const date = parseDate(selectedDate);
  date.setDate(date.getDate() + 1);

  return date.toLocaleDateString('fr-FR', { weekday: 'short' });
};

export const getPreviousDayName = (selectedDate: string): string => {
  const date = parseDate(selectedDate);
  date.setDate(date.getDate() - 1);

  return date.toLocaleDateString('fr-FR', { weekday: 'short' });
};

export const isDisciplineOption = (value: string): value is DISCIPLINE_OPTION => {
  return Object.values(DISCIPLINE_OPTION).includes(value as DISCIPLINE_OPTION);
};

export const isSortOption = (value: string): value is SORT_OPTION => {
  return Object.values(SORT_OPTION).includes(value as SORT_OPTION);
};

export const mapDisciplineToOption = (discipline: Discipline): DISCIPLINE_OPTION => {
  switch (discipline) {
    case Discipline.ATTELE:
      return DISCIPLINE_OPTION.ATTELE;
    case Discipline.MONTE:
      return DISCIPLINE_OPTION.MONTE;
    case Discipline.PLAT:
      return DISCIPLINE_OPTION.PLAT;
    case Discipline.CROSS:
      return DISCIPLINE_OPTION.CROSS;
    case Discipline.HAIE:
      return DISCIPLINE_OPTION.HAIE;
    case Discipline.STEEPLECHASE:
      return DISCIPLINE_OPTION.STEEPLECHASE;
    default:
      throw new Error(`Discipline inconnue: ${discipline}`);
  }
};

export const getPlafondColor = (
  reportPlafond?: number | null,
  coursePlafond?: number | null
) => {
  if (reportPlafond == null) return 'primary';
  if (coursePlafond == null) return 'primary';

  if (reportPlafond > coursePlafond * 1.15) return 'secondary';
  if (reportPlafond < coursePlafond * 0.80) return 'error.light';

  return 'primary';
};


// ===== CALCUL RATIO =====
export const getRatioGain = (gainsCheval: number, plafond?: number, recul?: number) => {
  if (gainsCheval == null) return null;

  // priorité au recul si applicable
  if (recul != null && gainsCheval < recul) {
    return gainsCheval / recul;
  }

  if (plafond != null) {
    return gainsCheval / plafond;
  }

  return null;
};

// ===== COULEUR UX SIMPLE =====
export const getRatioGainColor = (ratio: number, isDark: boolean) => {
  if (ratio == null) return 'primary.main';

  if (ratio >= 90) return 'success.main';   // très bien engagé
  if (ratio >= 75) return isDark ? "white" : 'primary.main';   // correct
  return 'warning.light';                       // mal engagé
};